import React, { useCallback, useEffect, useState } from "react";
import { Store, ShieldAlert, TrendingUp, AlertCircle } from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

export default function BusinessManager({ userStats, onActionSuccess }) {
  const [myBusinesses, setMyBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotify();

  // Fetches the player's current business portfolio
  const fetchMyBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      const data = await callBackend("/businesses/my-portfolio");
      setMyBusinesses(data);
    } catch (err) {
      addNotification("Failed to load business intelligence.", "error");
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchMyBusinesses();
  }, [fetchMyBusinesses]);

  // Handler for upgrading a business facility
  const handleUpgrade = async (bizId) => {
    try {
      const data = await callBackend(`/businesses/${bizId}/upgrade`, {
        method: "POST",
      });
      addNotification(data.message, "success");
      onActionSuccess(); // Updates global player stats like cash
      fetchMyBusinesses(); // Refresh local business levels
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  // Handler for deploying Thugs to protect a business
  const handleAssignGuards = async (bizId) => {
    const amountStr = window.prompt("How many Thugs do you want to deploy?");
    const amount = parseInt(amountStr);

    if (!amountStr) return;
    if (isNaN(amount) || amount <= 0) {
      return addNotification("Enter a valid number of muscle.", "error");
    }

    try {
      const data = await callBackend(`/businesses/${bizId}/assign-guards`, {
        method: "POST",
        body: { amount },
      });
      addNotification(data.message, "success");
      onActionSuccess(); // Updates global inventory counts
      fetchMyBusinesses(); // Refresh local guard counts
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  // Handler for ordering raw materials (Jewelry Stores only)
  const handleOrderStock = async (bizId) => {
    try {
      const data = await callBackend(`/businesses/${bizId}/order-stock`, {
        method: "POST",
      });
      addNotification(data.message, "success");
      onActionSuccess(); // Deduct cash from global stats
      fetchMyBusinesses(); // Refresh local stock levels
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 font-mono text-blue-500 animate-pulse">
        [ RETRIEVING PORTFOLIO DATA... ]
      </div>
    );
  }

  return (
    <div className="space-y-6 font-mono bg-black p-4 border-2 border-zinc-900">
      {/* Header */}
      <div className="flex justify-between items-center border-b-2 border-zinc-800 pb-4">
        <h2 className="text-4xl font-black italic text-white uppercase tracking-tighter skew-x-[-12deg] bg-zinc-900 px-4 py-1">
          Business Portfolio
        </h2>
      </div>

      {myBusinesses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-zinc-800 bg-zinc-950/50">
          <AlertCircle size={48} className="text-zinc-700 mb-4" />
          <h3 className="text-xl font-black text-zinc-500 uppercase italic">
            No Assets Detected
          </h3>
          <p className="text-xs text-zinc-600 mt-2 max-w-xs text-center uppercase font-bold">
            You don't own any legal fronts yet. Head to the Tactical Map to
            acquire properties in controlled districts.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {myBusinesses.map((biz) => (
            <div
              key={biz.id}
              className="bg-zinc-900 border-2 border-zinc-800 p-4 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-2 bg-zinc-800 text-zinc-500 group-hover:text-white transition-colors">
                <Store size={16} />
              </div>

              <div className="flex justify-between border-b border-zinc-700 pb-2 mb-4">
                <h3 className="text-xl font-black italic uppercase text-white leading-none">
                  {biz.name}
                </h3>
                <span className="text-[10px] bg-blue-600 px-2 py-0.5 text-black font-black skew-x-[-12deg]">
                  LVL {biz.level}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between text-[10px] font-bold uppercase">
                  <span className="text-zinc-500">Location</span>
                  <span className="text-white italic">{biz.district_name}</span>
                </div>

                <div className="flex justify-between text-[10px] font-bold uppercase">
                  <span className="text-zinc-500">Revenue (Net)</span>
                  <span className="text-green-500 font-black">
                    ${biz.total_earned_cash.toLocaleString()}
                  </span>
                </div>

                <div className="flex justify-between text-[10px] font-bold uppercase">
                  <span className="text-zinc-500">Security Detail</span>
                  <span className="text-red-500">
                    {biz.thugs_assigned} Thugs
                  </span>
                </div>

                {biz.category === "jewelry" && (
                  <div className="bg-black p-2 border border-zinc-800 mt-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[9px] text-zinc-400 uppercase font-black">
                        Workshop Stock
                      </span>
                      <span className="text-white text-[10px] font-bold">
                        {biz.stock_level} Units
                      </span>
                    </div>
                    <button
                      onClick={() => handleOrderStock(biz.id)}
                      className="w-full bg-zinc-800 hover:bg-white hover:text-black text-[9px] font-black py-1.5 uppercase transition-all"
                    >
                      Order Raw Materials ($25,000)
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-6">
                <button
                  onClick={() => handleAssignGuards(biz.id)}
                  className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-red-700 text-white py-2 text-[10px] font-black uppercase transition-all border border-transparent hover:border-white"
                >
                  <ShieldAlert size={14} /> Assign Guards
                </button>
                <button
                  onClick={() => handleUpgrade(biz.id)}
                  className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-blue-700 text-white py-2 text-[10px] font-black uppercase transition-all border border-transparent hover:border-white"
                >
                  <TrendingUp size={14} /> Upgrade
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
